import { Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import QuantityGridCell from './QuantityGridCell';
import { Util } from '../util/Util';
import { qtyMin } from '../util/quantity';

export type QuantityStepperColumnConfig<T extends { quantity: number; uniqueId: string }> = {
  quantityMin?: number;
  getQuantityMax?: (row: T) => number;
  quantityMaxByRowId?: Map<string, number>;
  onQuantityChangeByRowId: (rowId: string, newQuantity: number) => void;
  getIsFractional?: (row: T) => boolean;
};

export const applyQuantityStepperColumns = <T extends { quantity: number; uniqueId: string }>(
  columns: GridColDef<T>[],
  config: QuantityStepperColumnConfig<T>,
): GridColDef<T>[] => {
  const {
    quantityMin = 1,
    getQuantityMax,
    quantityMaxByRowId,
    onQuantityChangeByRowId,
    getIsFractional,
  } = config;

  return columns.map((col) => {
    if (col.field !== 'quantity') return col;

    const isEditable = col.editable === true;

    return {
      ...col,
      editable: false,
      align: 'center' as const,
      headerAlign: 'center' as const,
      cellClassName: isEditable ? 'qty-editable-cell' : undefined,
      renderCell: (params: GridRenderCellParams<T>) => {
        if (!isEditable) {
          return (
            <Typography variant="body2" sx={{ fontWeight: 600, width: '100%', textAlign: 'center' }}>
              {Util.formatNumber(params.row.quantity)}
            </Typography>
          );
        }

        const max = quantityMaxByRowId?.get(params.row.uniqueId)
          ?? getQuantityMax?.(params.row)
          ?? Number.MAX_SAFE_INTEGER;
        const fractional = getIsFractional?.(params.row) === true;
        const min = fractional ? Math.min(quantityMin, qtyMin(true)) : quantityMin;

        return (
          <QuantityGridCell
            rowId={params.row.uniqueId}
            value={params.row.quantity}
            min={min}
            max={max}
            fractional={fractional}
            onQuantityChangeByRowId={onQuantityChangeByRowId}
          />
        );
      },
    };
  });
};

// Theme-aware so the editable count cells stay readable in dark mode.
export const quantityStepperGridSx = {
  '& .MuiDataGrid-cell--editable, & .qty-editable-cell': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
    color: (theme: Theme) => `${theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark} !important`,
    border: (theme: Theme) => `1px dashed ${theme.palette.warning.main}`,
    fontWeight: '900 !important',
  },
  '& .MuiDataGrid-cell--editing, & .qty-editable-cell:focus-within': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.28 : 0.2),
  },
  '& .qty-editable-cell input': {
    color: (theme: Theme) => (theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark),
    fontWeight: 900,
  },
} as const;